'use client'

import { useState } from 'react'

import { Button } from '@/core/components/form/button/button'
import { InputText } from '@/core/components/form/input-text'
import { PasswordInput } from '@/core/components/form/password-input'
import SocialLogin from '@/features/veluvit/components/account/social-login'
import {
  Foot,
  ModalDesc,
  SheetHead,
  TextLink,
} from '@/features/veluvit/components/overlay/layer-parts'
import { INPUT_ZOOM_SAFE } from '@/features/veluvit/components/ui/field-class'
import {
  finishAuth,
  openAuth,
  continueAsGuest,
} from '@/features/veluvit/services/account.actions'
import { api } from '@/features/veluvit/services/api'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { Bootstrap } from '@/features/veluvit/types/api.types'

export type AuthMode = 'login' | 'register'

/** 영문·숫자·특수문자(영문·숫자·공백 외 문자)를 각각 하나 이상 */
const PASSWORD_PATTERN = String.raw`(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z\d\s]).{8,20}`

/** 로그인 · 계정 만들기 (v2 auth()) — 서버에 켜진 간편 로그인 + 이메일 */
export default function AuthModal({ mode }: { mode: AuthMode }) {
  const user = useSessionStore((s) => s.data?.user)
  const [error, setError] = useState('')
  const [pending, setPending] = useState(false)
  const register = mode === 'register'
  const desc = user?.guest
    ? register
      ? '지금의 대화와 컬렉션을 계정에 이어서 저장하세요.'
      : '기존 계정의 기록으로 로그인합니다. 지금 게스트 기록을 유지하려면 계정 만들기를 선택하세요.'
    : '나의 대화와 컬렉션을 이어서 만나세요.'

  const submit = async (form: HTMLFormElement) => {
    setPending(true)
    setError('')
    try {
      const values = Object.fromEntries(new FormData(form))
      const data = await api<Bootstrap>(`/auth/${mode}/`, values)
      await finishAuth(data, mode)
    } catch (e) {
      setError(e instanceof Error ? e.message : '다시 시도해 주세요.')
    } finally {
      setPending(false)
    }
  }

  return (
    <>
      <SheetHead title={register ? '계정 만들기' : '로그인'} />
      <ModalDesc>{desc}</ModalDesc>
      <SocialLogin className="mt-6" />
      <form
        className="mt-4.5 grid gap-3 text-left"
        onSubmit={(event) => {
          event.preventDefault()
          void submit(event.currentTarget)
        }}
      >
        {register && (
          <InputText
            label="이름"
            classNames={INPUT_ZOOM_SAFE}
            name="name"
            autoComplete="nickname"
            maxLength={40}
            defaultValue={user?.name || ''}
            required
          />
        )}
        <InputText
          label="이메일"
          classNames={INPUT_ZOOM_SAFE}
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          maxLength={254}
        />
        <PasswordInput
          label="비밀번호"
          classNames={INPUT_ZOOM_SAFE}
          name="password"
          autoComplete={register ? 'new-password' : 'current-password'}
          // 백엔드 검증기: 영문·숫자·특수문자를 모두 넣은 8~20자. 로그인은 예전 비밀번호도 받아야 해서 가입일 때만 건다
          minLength={register ? 8 : undefined}
          maxLength={register ? 20 : 128}
          pattern={register ? PASSWORD_PATTERN : undefined}
          title={
            register
              ? '영문·숫자·특수문자를 모두 넣어 8~20자로 입력해 주세요'
              : undefined
          }
          required
          placeholder={register ? '영문·숫자·특수문자 포함 8~20자' : undefined}
        />
        <p
          role="alert"
          className="text-c13 leading-[1.6] whitespace-pre-wrap text-error"
        >
          {error}
        </p>
        <Button
          variant="accent"
          size="lg"
          shape="pill"
          bold
          className="w-full"
          type="submit"
          disabled={pending}
        >
          {register ? '계정 만들기' : '로그인'}
        </Button>
      </form>
      <div className="mt-3.5 flex flex-wrap justify-center gap-3">
        <TextLink onClick={() => openAuth(register ? 'login' : 'register')}>
          {register ? '이미 계정이 있어요' : '처음 오셨나요? 계정 만들기'}
        </TextLink>
        <TextLink onClick={continueAsGuest}>게스트로 계속하기</TextLink>
      </div>
      <Foot>
        게스트 기록은 이 브라우저에서 이어집니다. 계정을 만들면 다시 로그인해
        불러올 수 있어요.
      </Foot>
    </>
  )
}
